import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings, AVAILABLE_HEADING_FONTS, AVAILABLE_BODY_FONTS } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { LogOut, Plus, Edit, Trash2, Upload, X, FileDown } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import './AdminPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyArtwork = {
  title: '',
  year: new Date().getFullYear().toString(),
  medium: '',
  dimensions: '',
  image: '',
  detail: '',
  scene: '',
  category: 'works',
  available: true,
  price: 0,
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { settings, updateSettings } = useSettings();
  const [siteSettings, setSiteSettings] = useState(settings);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [artworks, setArtworks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyArtwork);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/');
      return;
    }
    fetchArtworks();
    fetchOrders();
    fetchStats();
    fetchOffers();
  }, [user, loading, navigate]);

  const fetchOffers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/offers`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setOffers(data);
      }
    } catch (err) {
      console.error('Fetch offers error:', err);
    }
  };

  const updateOfferStatus = async (offerId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/offers/${offerId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        await fetchOffers();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/offers/${offerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        await fetchOffers();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Sync site settings when context changes
  useEffect(() => {
    setSiteSettings(settings);
  }, [settings]);

  const saveSiteSettings = async () => {
    setSavingSettings(true);
    const ok = await updateSettings(siteSettings);
    setSavingSettings(false);
    if (ok) {
      alert('Settings saved');
    } else {
      alert('Failed to save settings');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      }
    } catch (err) {
      console.error('Fetch orders error:', err);
    }
  };

  const confirmTransferPayment = async (orderId) => {
    if (!window.confirm('Mark this order as PAID? This will also mark the artworks as sold.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/confirm-payment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: '' }),
      });
      if (response.ok) {
        await fetchOrders();
        await fetchStats();
        await fetchArtworks();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Could not confirm: ' + (err.detail || 'unknown error'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const setShippingQuote = async (orderId, currentSubtotal) => {
    const input = window.prompt(`Enter the international shipping cost in USD (added to subtotal $${currentSubtotal}):`, '150');
    if (input === null) return;
    const amount = parseFloat(input);
    if (Number.isNaN(amount) || amount < 0) {
      alert('Please enter a valid amount');
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/set-shipping`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shipping_amount: amount }),
      });
      if (response.ok) {
        await fetchOrders();
        await fetchStats();
        const data = await response.json();
        const url = data.paypal_pay_url;
        if (url) {
          if (window.confirm('Shipping set. Open the PayPal payment link to copy and send to the customer?')) {
            window.open(url, '_blank');
          }
        }
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Could not set shipping: ' + (err.detail || 'unknown error'));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Cancel this pending order?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        await fetchOrders();
        await fetchStats();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/orders/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Fetch stats error:', err);
    }
  };

  const fetchArtworks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/artworks`);
      if (response.ok) {
        const data = await response.json();
        setArtworks(data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, [field]: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const method = editingArtwork ? 'PUT' : 'POST';
      const url = editingArtwork
        ? `${BACKEND_URL}/api/admin/artworks/${editingArtwork.id}`
        : `${BACKEND_URL}/api/admin/artworks`;

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price) || 0,
        }),
      });

      if (response.ok) {
        await fetchArtworks();
        resetForm();
      } else {
        alert('Error saving artwork');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this artwork? This cannot be undone.')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/artworks/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        await fetchArtworks();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleEdit = (artwork) => {
    setEditingArtwork(artwork);
    setFormData(artwork);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData(emptyArtwork);
    setEditingArtwork(null);
    setShowForm(false);
  };

  const handleSeed = async () => {
    if (!window.confirm('Seed initial demo artworks? Only works if database is empty.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/seed`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        await fetchArtworks();
        alert('Database seeded');
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (!user || !user.is_admin) {
    return null;
  }

  const filteredArtworks = filter === 'all' 
    ? artworks 
    : artworks.filter(a => a.category === filter);

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-header-left">
          <h1 className="admin-logo">C. LLAGUNO</h1>
          <span className="admin-label">Admin Panel</span>
        </div>
        <div className="admin-header-right">
          <span className="admin-user">{user.email}</span>
          <button className="admin-btn-icon" onClick={() => navigate('/')} data-testid="back-home-btn">
            View Site
          </button>
          <button className="admin-btn-icon" onClick={logout} data-testid="logout-btn">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="admin-main">
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
            data-testid="tab-dashboard"
          >
            Dashboard
          </button>
          <button 
            className={`admin-tab ${activeTab === 'artworks' ? 'active' : ''}`}
            onClick={() => setActiveTab('artworks')}
            data-testid="tab-artworks"
          >
            Artworks ({artworks.length})
          </button>
          <button 
            className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => { setActiveTab('orders'); fetchOrders(); fetchStats(); }}
            data-testid="tab-orders"
          >
            Orders ({orders.length})
          </button>
          <button 
            className={`admin-tab ${activeTab === 'offers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('offers'); fetchOffers(); }}
            data-testid="tab-offers"
          >
            Offers ({offers.filter(o => o.status === 'new').length}/{offers.length})
          </button>
          <button 
            className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
            data-testid="tab-settings"
          >
            Settings
          </button>
        </div>

        {activeTab === 'dashboard' && <AdminDashboard />}

        {activeTab === 'artworks' && (
        <>
        <div className="admin-toolbar">
          <div className="admin-filters">
            <button 
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({artworks.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'works' ? 'active' : ''}`}
              onClick={() => setFilter('works')}
            >
              Works ({artworks.filter(a => a.category === 'works').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'sculpture' ? 'active' : ''}`}
              onClick={() => setFilter('sculpture')}
            >
              White Dolls ({artworks.filter(a => a.category === 'sculpture').length})
            </button>
          </div>
          <div className="admin-actions">
            <a
              href={`${BACKEND_URL}/api/admin/catalog.pdf?lang=es`}
              className="admin-btn-secondary"
              data-testid="download-catalog-es"
              rel="noopener"
            >
              <FileDown size={18} /> Catálogo PDF (ES)
            </a>
            <a
              href={`${BACKEND_URL}/api/admin/catalog.pdf?lang=en`}
              className="admin-btn-secondary"
              data-testid="download-catalog-en"
              rel="noopener"
            >
              <FileDown size={18} /> Catalogue PDF (EN)
            </a>
            {artworks.length === 0 && (
              <button className="admin-btn-secondary" onClick={handleSeed}>
                Seed Demo Data
              </button>
            )}
            <button 
              className="admin-btn-primary" 
              onClick={() => { resetForm(); setShowForm(true); }}
              data-testid="new-artwork-btn"
            >
              <Plus size={18} /> New Artwork
            </button>
          </div>
        </div>

        {filteredArtworks.length === 0 ? (
          <div className="admin-empty">
            <p>No artworks yet. Add your first piece.</p>
          </div>
        ) : (
          <div className="admin-grid">
            {filteredArtworks.map(artwork => (
              <div key={artwork.id} className="admin-card" data-testid={`admin-card-${artwork.id}`}>
                <div className="admin-card-image">
                  <img src={artwork.image} alt={artwork.title} />
                  <div className={`admin-card-status ${artwork.available ? 'available' : 'sold'}`}>
                    {artwork.available ? 'Available' : 'Sold'}
                  </div>
                </div>
                <div className="admin-card-info">
                  <h3>{artwork.title}</h3>
                  <p className="admin-card-meta">
                    {artwork.category === 'sculpture' ? 'White Dolls' : 'Works'} • {artwork.year}
                  </p>
                  <p className="admin-card-price">${Number(artwork.price).toLocaleString('en-US', { maximumFractionDigits: 0 })} USD</p>
                </div>
                <div className="admin-card-actions">
                  <button onClick={() => handleEdit(artwork)} data-testid={`edit-${artwork.id}`}>
                    <Edit size={16} /> Edit
                  </button>
                  <button onClick={() => handleDelete(artwork.id)} className="danger" data-testid={`delete-${artwork.id}`}>
                    <Trash2 size={16} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {activeTab === 'orders' && (
          <div className="orders-section">
            {stats && (
              <div className="orders-stats">
                <div className="stat-card">
                  <span className="stat-label">Total Revenue</span>
                  <span className="stat-value">${Number(stats.total_revenue).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Paid Orders</span>
                  <span className="stat-value">{stats.paid_orders}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Pending</span>
                  <span className="stat-value">{stats.pending_orders}</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Total Orders</span>
                  <span className="stat-value">{stats.total_orders}</span>
                </div>
              </div>
            )}

            {orders.length === 0 ? (
              <div className="admin-empty">
                <p>No orders yet. When customers buy artworks, they'll appear here.</p>
              </div>
            ) : (
              <div className="orders-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Reference</th>
                      <th>Method</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id} data-testid={`order-${order.id}`}>
                        <td>{new Date(order.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'short', day: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}</td>
                        <td className="mono">{order.reference || order.session_id?.slice(-12) || '—'}</td>
                        <td>
                          <span className={`pm-badge pm-${order.payment_method || 'stripe'}`}>
                            {order.payment_method === 'transfer' ? 'Transfer' : 'Stripe'}
                          </span>
                        </td>
                        <td>
                          {order.customer_name || order.customer_email ? (
                            <div className="order-customer">
                              {order.customer_name && <span>{order.customer_name}</span>}
                              {order.customer_email && (
                                <a href={`mailto:${order.customer_email}`} className="offer-email">{order.customer_email}</a>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td>
                          <div className="order-items">
                            {order.items?.map((item, idx) => (
                              <span key={idx} className="order-item-tag">
                                {item.title}
                                {item.frame ? ` · ${item.frame}` : ''}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="amount">${Number(order.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td>
                          <span className={`order-status status-${order.payment_status}`}>
                            {order.payment_status}
                          </span>
                        </td>
                        <td>
                          {order.shipping_quote_required && order.payment_status === 'pending' && (
                            <button
                              className="admin-btn-primary admin-btn-xs"
                              onClick={() => setShippingQuote(order.id, order.subtotal)}
                              data-testid={`set-shipping-${order.id}`}
                              style={{ marginBottom: 6 }}
                            >
                              Set shipping
                            </button>
                          )}
                          {order.payment_status === 'pending' && !order.shipping_quote_required && (
                            <div className="offer-actions">
                              <button
                                className="admin-btn-primary admin-btn-xs"
                                onClick={() => confirmTransferPayment(order.id)}
                                data-testid={`confirm-payment-${order.id}`}
                              >
                                Mark as paid
                              </button>
                              <button
                                className="offer-delete-btn"
                                onClick={() => cancelOrder(order.id)}
                                title="Cancel"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'offers' && (
          <div className="orders-section">
            {offers.length === 0 ? (
              <div className="admin-empty">
                <p>No offers yet. When customers make an offer on an artwork, it'll appear here.</p>
              </div>
            ) : (
              <div className="orders-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Artwork</th>
                      <th>From</th>
                      <th>Offer</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map(offer => (
                      <tr key={offer.id} data-testid={`offer-${offer.id}`}>
                        <td>{new Date(offer.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}</td>
                        <td>{offer.artwork_title}</td>
                        <td>
                          <div className="offer-contact">
                            <span>{offer.name}</span>
                            <a href={`mailto:${offer.email}`} className="offer-email">{offer.email}</a>
                          </div>
                        </td>
                        <td className="amount">${Number(offer.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                        <td className="offer-message-cell">{offer.message || '—'}</td>
                        <td>
                          <span className={`order-status status-${offer.status}`}>
                            {offer.status}
                          </span>
                        </td>
                        <td>
                          <div className="offer-actions">
                            <select 
                              value={offer.status} 
                              onChange={(e) => updateOfferStatus(offer.id, e.target.value)}
                              className="offer-status-select"
                            >
                              <option value="new">New</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="accepted">Accepted</option>
                              <option value="rejected">Rejected</option>
                            </select>
                            <button 
                              className="offer-delete-btn"
                              onClick={() => deleteOffer(offer.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <div className="settings-card">
              <h3 className="settings-title">Typography</h3>
              <p className="settings-help">Change fonts used across the site. Changes apply immediately to all visitors.</p>

              <div className="settings-row">
                <div className="settings-field">
                  <label>Heading font (titles, logo)</label>
                  <select
                    value={siteSettings.heading_font}
                    onChange={(e) => setSiteSettings({ ...siteSettings, heading_font: e.target.value })}
                    style={{ fontFamily: `'${siteSettings.heading_font}', serif`, fontSize: '1.1rem' }}
                    data-testid="heading-font-select"
                  >
                    {AVAILABLE_HEADING_FONTS.map(font => (
                      <option key={font} value={font} style={{ fontFamily: `'${font}', serif` }}>
                        {font}
                      </option>
                    ))}
                  </select>
                  <div className="font-preview" style={{ fontFamily: `'${siteSettings.heading_font}', serif` }}>
                    C. LLAGUNO
                  </div>
                </div>

                <div className="settings-field">
                  <label>Body font (paragraphs, buttons)</label>
                  <select
                    value={siteSettings.body_font}
                    onChange={(e) => setSiteSettings({ ...siteSettings, body_font: e.target.value })}
                    style={{ fontFamily: `'${siteSettings.body_font}', sans-serif` }}
                  >
                    {AVAILABLE_BODY_FONTS.map(font => (
                      <option key={font} value={font}>
                        {font}
                      </option>
                    ))}
                  </select>
                  <div className="font-preview small" style={{ fontFamily: `'${siteSettings.body_font}', sans-serif` }}>
                    Contemporary Artist
                  </div>
                </div>
              </div>
            </div>

            <div className="settings-card">
              <h3 className="settings-title">Display Options</h3>
              <p className="settings-help">Control what is shown on artwork cards in the gallery view (not the detail modal).</p>

              <div className="settings-toggles">
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={siteSettings.show_works_year}
                    onChange={(e) => setSiteSettings({ ...siteSettings, show_works_year: e.target.checked })}
                  />
                  <span>Show year on Works cards</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={siteSettings.show_works_medium}
                    onChange={(e) => setSiteSettings({ ...siteSettings, show_works_medium: e.target.checked })}
                  />
                  <span>Show medium on Works cards</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={siteSettings.show_sculptures_year}
                    onChange={(e) => setSiteSettings({ ...siteSettings, show_sculptures_year: e.target.checked })}
                  />
                  <span>Show year on White Dolls cards</span>
                </label>
                <label className="settings-toggle">
                  <input
                    type="checkbox"
                    checked={siteSettings.show_sculptures_medium}
                    onChange={(e) => setSiteSettings({ ...siteSettings, show_sculptures_medium: e.target.checked })}
                  />
                  <span>Show medium on White Dolls cards</span>
                </label>
              </div>
            </div>

            <div className="settings-card">
              <h3 className="settings-title">PayPal & Shipping</h3>
              <p className="settings-help">Your PayPal.me link. Customers will be redirected here to complete payment.</p>
              <div className="settings-field">
                <label>PayPal.me link</label>
                <input
                  type="url"
                  value={siteSettings.paypal_link || ''}
                  placeholder="https://paypal.me/CarlotaT"
                  onChange={(e) => setSiteSettings({ ...siteSettings, paypal_link: e.target.value })}
                  data-testid="paypal-link-input"
                />
              </div>
              <div className="settings-field">
                <label>Free shipping inside Mexico</label>
                <p className="settings-help" style={{ marginTop: '0.25rem' }}>
                  Inside Mexico: Free · International: quoted per order from the admin once you receive it.
                </p>
              </div>
            </div>

            <div className="settings-card">
              <h3 className="settings-title">Bank Transfer Details</h3>
              <p className="settings-help">These details are shown to customers who choose "Direct Bank Transfer" at checkout. Leave any field blank if it doesn't apply.</p>

              <div className="settings-row">
                <div className="settings-field">
                  <label>Bank name</label>
                  <input
                    type="text"
                    value={siteSettings.bank_name || ''}
                    placeholder="e.g. BBVA México"
                    onChange={(e) => setSiteSettings({ ...siteSettings, bank_name: e.target.value })}
                    data-testid="bank-name-input"
                  />
                </div>
                <div className="settings-field">
                  <label>Account holder</label>
                  <input
                    type="text"
                    value={siteSettings.bank_account_holder || ''}
                    placeholder="Carlota Llaguno"
                    onChange={(e) => setSiteSettings({ ...siteSettings, bank_account_holder: e.target.value })}
                  />
                </div>
              </div>

              <div className="settings-row">
                <div className="settings-field">
                  <label>Account number</label>
                  <input
                    type="text"
                    value={siteSettings.bank_account_number || ''}
                    onChange={(e) => setSiteSettings({ ...siteSettings, bank_account_number: e.target.value })}
                  />
                </div>
                <div className="settings-field">
                  <label>CLABE (Mexico)</label>
                  <input
                    type="text"
                    value={siteSettings.bank_clabe || ''}
                    onChange={(e) => setSiteSettings({ ...siteSettings, bank_clabe: e.target.value })}
                  />
                </div>
              </div>

              <div className="settings-row">
                <div className="settings-field">
                  <label>SWIFT / BIC (international)</label>
                  <input
                    type="text"
                    value={siteSettings.bank_swift || ''}
                    onChange={(e) => setSiteSettings({ ...siteSettings, bank_swift: e.target.value })}
                  />
                </div>
                <div className="settings-field">
                  <label>Reference note (optional)</label>
                  <input
                    type="text"
                    value={siteSettings.bank_reference_note || ''}
                    placeholder="e.g. Please include your order reference in the concept"
                    onChange={(e) => setSiteSettings({ ...siteSettings, bank_reference_note: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="settings-actions">
              <button 
                className="admin-btn-primary" 
                onClick={saveSiteSettings} 
                disabled={savingSettings}
                data-testid="save-settings-btn"
              >
                {savingSettings ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </main>

      {showForm && (
        <div className="form-overlay" onClick={resetForm}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <button className="form-close" onClick={resetForm}>
              <X size={20} />
            </button>
            <h2>{editingArtwork ? 'Edit Artwork' : 'New Artwork'}</h2>

            <form onSubmit={handleSubmit} className="artwork-form">
              <div className="form-row">
                <div className="form-field">
                  <label>Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="form-title"
                  />
                </div>
                <div className="form-field">
                  <label>Year *</label>
                  <input
                    type="text"
                    required
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Medium *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Mixed Media on Canvas"
                    value={formData.medium}
                    onChange={(e) => setFormData({ ...formData, medium: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Dimensions *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 120 x 100 cm"
                    value={formData.dimensions}
                    onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="works">Works</option>
                    <option value="sculpture">White Dolls</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Price (USD) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.available}
                    onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                  />
                  {' '}Available for sale
                </label>
              </div>

              <div className="form-images">
                <div className="image-upload-group">
                  <label>Full Artwork Image *</label>
                  {formData.image && <img src={formData.image} alt="Full" className="preview-img" />}
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'image')} />
                  <input
                    type="text"
                    placeholder="Or paste image URL"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  />
                </div>

                {formData.category !== 'sculpture' && (
                  <div className="image-upload-group">
                    <label>Detail Image *</label>
                    {formData.detail && <img src={formData.detail} alt="Detail" className="preview-img" />}
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'detail')} />
                    <input
                      type="text"
                      placeholder="Or paste image URL"
                      value={formData.detail}
                      onChange={(e) => setFormData({ ...formData, detail: e.target.value })}
                    />
                  </div>
                )}

                <div className="image-upload-group">
                  <label>In Scene Image *</label>
                  {formData.scene && <img src={formData.scene} alt="Scene" className="preview-img" />}
                  <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'scene')} />
                  <input
                    type="text"
                    placeholder="Or paste image URL"
                    value={formData.scene}
                    onChange={(e) => setFormData({ ...formData, scene: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-buttons">
                <button type="button" onClick={resetForm} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn-save" data-testid="form-save">
                  {saving ? 'Saving...' : (editingArtwork ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
